import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import type { Member, MemberType } from '@shared/api.interface';

export interface MemberSession {
  passcode: string;
  member: Member;
  remainingCycle: number;
  remainingToday: number;
}

interface MemberContextType {
  session: MemberSession | null;
  memberType: MemberType | null;
  isAuthenticated: boolean;
  login: (session: MemberSession) => void;
  logout: () => void;
  updateQuota: (remainingCycle: number, remainingToday: number) => void;
  refreshSession: (data: {
    member: Member;
    remainingCycle: number;
    remainingToday: number;
  }) => void;
}

const MemberContext = createContext<MemberContextType | undefined>(undefined);

const SESSION_KEY = 'member_session';

export function MemberProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<MemberSession | null>(() => {
    if (typeof window !== 'undefined') {
      const saved = sessionStorage.getItem(SESSION_KEY);
      if (saved) {
        try {
          return JSON.parse(saved);
        } catch {
          return null;
        }
      }
    }
    return null;
  });

  useEffect(() => {
    if (session) {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
    } else {
      sessionStorage.removeItem(SESSION_KEY);
    }
  }, [session]);

  const login = useCallback((newSession: MemberSession) => {
    setSession(newSession);
  }, []);

  const logout = useCallback(() => {
    setSession(null);
    sessionStorage.removeItem(SESSION_KEY);
  }, []);

  const updateQuota = useCallback((remainingCycle: number, remainingToday: number) => {
    setSession(prev => {
      if (!prev) return null;
      return {
        ...prev,
        remainingCycle,
        remainingToday,
      };
    });
  }, []);

  const refreshSession = useCallback((data: {
    member: Member;
    remainingCycle: number;
    remainingToday: number;
  }) => {
    setSession(prev => {
      if (!prev) return null;
      return {
        ...prev,
        member: data.member,
        remainingCycle: data.remainingCycle,
        remainingToday: data.remainingToday,
      };
    });
  }, []);

  const value: MemberContextType = {
    session,
    memberType: session?.member.member_type || null,
    isAuthenticated: !!session,
    login,
    logout,
    updateQuota,
    refreshSession,
  };

  return (
    <MemberContext.Provider value={value}>
      {children}
    </MemberContext.Provider>
  );
}

export function useMember() {
  const context = useContext(MemberContext);
  if (context === undefined) {
    throw new Error('useMember must be used within a MemberProvider');
  }
  return context;
}
